import React, { useState } from 'react';
import { KiteButton, KiteInput } from '@kite/react-kite';
import { useLocation, useHistory } from 'react-router-dom';
import AssetCards from '../AssetCards';
import ServerError from '../ServerError';
import { updateAsset } from '../../apiCalls/AssetsService';
import * as ContentService from '../../apiCalls/ContentService';
import './AttachServiceToAssetForm.scss';

interface ISortedFiles {
  fp: {
    [key: string]: {
      language: string;
      languageError: boolean;
    };
  };
  service: {
    [key: string]: {
      language: string;
      languageError: boolean;
      serviceType: string;
      serviceTypeError: boolean;
    };
  };
}

interface IFpFile {
  id: string;
  languages: string;
  file: string;
}

interface IServiceFile {
  id: string;
  serviceType: string;
  language: string;
}

interface IProcessedFiles {
  fp: IFpFile[];
  service: IServiceFile[];
}

interface IAttachServiceToAssetFormProps {
  handleBackClick: () => void;
  handleFileSelect: (type: any, file: any, field: any, value: any) => void;
  sortedFiles: ISortedFiles;
}

interface IServiceAssetFormStateProps {
  movieTmsid: string;
  movieTmsidError: boolean;
}

const initialServiceAssetFormState = {
  movieTmsid: '',
  movieTmsidError: false,
};

const AttachServiceToAssetForm = ({
  handleBackClick,
  sortedFiles,
  handleFileSelect,
}: IAttachServiceToAssetFormProps) => {
  const [serviceAssetFormState, setServiceAssetFormState] = useState<
    IServiceAssetFormStateProps
  >(initialServiceAssetFormState);
  const [assetFiles, setAssetFiles] = useState<ISortedFiles>(sortedFiles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const location = useLocation();
  const history = useHistory();
  const pathSegments = location.pathname.split('/');
  const assetId = pathSegments[pathSegments.length - 1];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setServiceAssetFormState((prev) => ({
      ...prev,
      [name]: value.trim(),
      [name + 'Error']: false,
    }));
  };

  const validateTmsid = (tmsid: string) =>
    !!(tmsid && tmsid.length === 14 && tmsid.startsWith('MV'));

  const validateFormAndFiles = () => {
    // Validate Form
    const { movieTmsid } = serviceAssetFormState;
    let hasError = false;

    const isTmsidValid = validateTmsid(movieTmsid);

    setServiceAssetFormState((prev) => {
      return {
        ...prev,
        movieTmsidError: !isTmsidValid,
      };
    });
    hasError = !isTmsidValid;

    // Validate Files
    const validatedFiles = { ...sortedFiles };
    const processedFiles: IProcessedFiles = {
      fp: [], // function does not check errors for fingerprint files
      service: [],
    };

    for (const [file, details] of Object.entries(assetFiles.service)) {
      const serviceFile = assetFiles.service[file];
      const validatedFile = validatedFiles.service[file];

      if (!serviceFile.language || !serviceFile.serviceType) {
        hasError = true;
        validatedFile.languageError = !details.language;
        validatedFile.serviceTypeError = !details.serviceType;
      }

      if (!hasError) {
        const formattedFile: IServiceFile = {
          id: file,
          serviceType: serviceFile.serviceType,
          language: serviceFile.language,
        };
        processedFiles.service.push(formattedFile);
      }
    }

    if (hasError) {
      setAssetFiles(validatedFiles);
    }
    return { hasError, processedFiles };
  };
  const handleAddService = async () => {
    const { hasError, processedFiles } = validateFormAndFiles();
    if (hasError) return;

    setLoading(true);
    try {
      const servicePromises = processedFiles.service.map((serviceFile) => {
        return ContentService.createContent(
          serviceFile.id,
          serviceFile.language,
          serviceFile.serviceType,
          serviceAssetFormState.movieTmsid,
          'QualityControl'
        );
      });

      const serviceResponses = await Promise.all(servicePromises);

      if (serviceResponses.every((response) => response === undefined)) {
        console.error('All service responses failed.');
        setError(true);
        return;
      }

      const validResponse = serviceResponses.find(
        (response: any) => response?.movie?.id
      );
      if (!validResponse) {
        console.error('No valid response ID found.');
        setError(true);
        return;
      }

      const assetResponse = await updateAsset(assetId, {
        tmsId: `${serviceAssetFormState.movieTmsid} `, // extra space at the end to make tmsId unique
        newAsset: false,
      });

      const {
        movie: { id },
      } = validResponse;

      // Use React Router to navigate
      history.push(`/movies/${id}`);
    } catch (err) {
      console.error('Error while adding services', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="attach-service">
      <h1 className="attach-service__heading">
        Attach Service to an Existing Asset
      </h1>
      <div className="attach-service__form-container">
        <h2 className="attach-service__form-one-heading">Asset Metadata</h2>
        <p className="attach-service__form-one-text">
          All fields required unless otherwise noted
        </p>
        <div className="attach-service__form-container__inputs">
          <KiteInput
            label="TMSID:"
            maxWidth="472px"
            placeholder="MV017981150000"
            id="movieTmsid"
            name="movieTmsid"
            value={serviceAssetFormState.movieTmsid}
            onChange={(e) => handleChange(e)}
            errorMessage={
              serviceAssetFormState.movieTmsidError
                ? 'Please enter a valid TMSID'
                : ''
            }
          />
        </div>
        <div className="attach-service__form-container__assets">
          <AssetCards
            files={assetFiles}
            onSelect={handleFileSelect}
            filePrefix="assets/movies/"
          />
        </div>
      </div>
      <div className="attach-service__container-buttons">
        <KiteButton type="outline" onClick={() => handleBackClick()}>
          Back
        </KiteButton>
        <KiteButton type="primary" onClick={handleAddService} loading={loading}>
          Add Service
        </KiteButton>
      </div>
      <ServerError isOpen={error} onClose={() => setError(false)} />
    </div>
  );
};

export default AttachServiceToAssetForm;
